import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Breakpoints,
  SelectorType,
  Tab,
  TabDesign,
  TabLayout,
  Tabs,
  useBreakpoint,
  useTranslation,
} from '@nextgen-web-framework/all';
import { PageFilterProps, QuickFilterKey } from './types/propTypes';
import styles from './PageFilter.module.scss';
import PageFilterSkeleton from './PageFilterSkeleton';
export const getBadge = (badgeCount: number) => badgeCount > 0 && <div className={styles.badge}>{badgeCount}</div>;

const PageFilter = ({
  isSortAndFilterButtonActive,
  appliedQuickFilter,
  availableQuickFilters,
  badgeCount,
  isPLP,
  toggleSortAndFilter,
  updateQuickFilter,
}: PageFilterProps) => {
  const [selectedQuickFilter, setSelectedQuickFilter] = useState(0);
  const currentSelectedFilterTab = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const viewPortType = useBreakpoint() as Breakpoints;
  const t = useTranslation();
  const { XS, SM, MD, LG, XL } = Breakpoints;
  const { ALL, NEW, PROMOTION, SUBSCRIPTION, TRENDING } = QuickFilterKey;
  const isDisabled = (key: QuickFilterKey) => !availableQuickFilters.includes(key);

  const { NEXT_SOP_QUICK_FILTER_HIDE: isSOPQuickFilterHide = false } = ConfigService.getConfigs() as {
    NEXT_SOP_QUICK_FILTER_HIDE?: boolean;
  };

  const quickFilters = useMemo(() => {
    const tabsData = [
      { title: t('search.searchResult.all'), id: ALL },
      { title: t('search.searchResult.new'), id: NEW, disabled: isDisabled(NEW) },
      { title: t('search.searchResult.promos'), id: PROMOTION, disabled: isDisabled(PROMOTION) },
      { title: t('search.searchResult.trending'), id: TRENDING, disabled: isDisabled(TRENDING) },
    ];
    if (!isSOPQuickFilterHide) {
      tabsData.push({
        title: t('search.searchResult.subscriptions'),
        id: SUBSCRIPTION,
        disabled: isDisabled(SUBSCRIPTION),
      });
    }
    return tabsData;
  }, [availableQuickFilters]);

  useEffect(() => {
    let position = 0;
    switch (appliedQuickFilter) {
      case ALL:
        position = 0;
        break;
      case NEW:
        position = 1;
        break;
      case PROMOTION:
        position = 2;
        break;
      case TRENDING:
        position = 3;
        break;
      case SUBSCRIPTION:
        position = 4;
        break;
      default:
        position = 0;
    }
    currentSelectedFilterTab.current = position;
    setSelectedQuickFilter(position);
  }, [appliedQuickFilter]);

  useEffect(() => {
    const callback = () => {
      const top = viewPortType === MD ? 80 : 60;
      if (containerRef.current && containerRef.current?.getBoundingClientRect().top <= top) {
        containerRef.current.classList.add(styles['sticky']);
      } else {
        containerRef.current?.classList.remove(styles['sticky']);
      }
    };

    if ([XS, SM, MD].includes(viewPortType) && containerRef.current) {
      document.addEventListener('scroll', callback);
    }

    return () => document.removeEventListener('scroll', callback);
  }, [containerRef.current, viewPortType]);

  const handleQuickFiltersClick = (tab: number, id?: string) => {
    if (currentSelectedFilterTab.current != tab) {
      setSelectedQuickFilter(tab);
      currentSelectedFilterTab.current = tab;
      updateQuickFilter(String(id ?? ''));
    } else {
      setSelectedQuickFilter(0);
      currentSelectedFilterTab.current = 0;
      updateQuickFilter('');
    }
  };

  return (
    <div
      ref={containerRef}
      id="page-filter"
      className={`${styles['search-page-filter']} ${!isPLP && styles['no-border']}`}
    >
      {![XS, SM].includes(viewPortType) && (
        <>
          <div className={styles['sort-and-filter-btn-container']}>
            <Tab
              data-testid="search-sort-&-filter-btn-testid"
              title={
                [LG, XL].includes(viewPortType) && isSortAndFilterButtonActive
                  ? t('search.searchResult.hideSortAndFilter')
                  : t('search.common.sortAndFilter')
              }
              icon="sliders"
              design={TabDesign.PILL}
              layout={TabLayout.HORIZONTAL}
              selectorType={SelectorType.CIRCLE}
              selected={isSortAndFilterButtonActive}
              onTabClick={toggleSortAndFilter}
            />
            {getBadge(badgeCount)}
          </div>
          <div className={styles['filter-divider']}></div>
        </>
      )}
      <Tabs
        tabList={quickFilters}
        selectedTab={selectedQuickFilter}
        design={TabDesign.PILL}
        selectorType={SelectorType.CIRCLE}
        onTabClick={handleQuickFiltersClick}
        className={styles['quick-filters']}
      />
    </div>
  );
};

export default PageFilter;
export { PageFilterSkeleton };
